/**
 * Where each presentation page lives, written once.
 *
 * Spanish slugs like the rest of the pipeline (`/pipeline/b/:ref/presentacion`,
 * a line `/l/:lineKey`, a shot's edit mode `/l/:lineKey?plano=s07`, the deck `/deck`), keyed by the concept's ref because that is what people say and
 * paste, and by line and shot *keys* rather than positions, so a link to `s07`
 * still lands on that shot after somebody reorders the script. The router in
 * `app.tsx`, the layout's width rules and the nav's active state match the same
 * shapes; a page that links anywhere in the editor builds the URL here rather
 * than by hand.
 *
 * Not to be confused with `presentationPath` in `@scandi/shared`, which is a
 * hosted HTML deck's `/p/<slug>/`.
 */
import { briefPath } from "@/features/pipeline/brief-path";

const part = encodeURIComponent;

/** The summary: the whole presentation on one page. Strategists and admins. */
export const presentationEditorPath = (ref: string) => `${briefPath(ref)}/presentacion`;

/**
 * The script in edit mode: lines, timing, add, remove, reorder - a mode of
 * the presentation page, not a page of its own. The old `/guion` route
 * redirects here.
 */
export const scriptPath = (ref: string) => `${presentationEditorPath(ref)}?edit=1`;

/** One line: its words, voices, note and shots in detail. */
export const linePath = (ref: string, lineKey: string) => `${presentationEditorPath(ref)}/l/${part(lineKey)}`;

/**
 * The search parameter that names one shot on its line page (`?plano=s07`):
 * the page scrolls to its card. There is no shot page: a shot is edited in
 * place, where it sits among the line's shots.
 */
export const SHOT_PARAM = "plano";

/**
 * One shot, on its line page. Every link to a shot comes
 * here - the preview player, the bell, the Spend page, the Generations pill
 * (which adds `?abrir=` for a popup). The line page finds the shot by its
 * key, which is unique in the storyboard, so a link from before the shot
 * moved is put right there; the old `/l/:lineKey/s/:shotKey` address
 * redirects here.
 */
export const shotPath = (ref: string, lineKey: string, shotKey: string) =>
  `${linePath(ref, lineKey)}?${SHOT_PARAM}=${part(shotKey)}`;

/**
 * The line segment of a link to a shot whose line the linker does not know,
 * or that belongs to no line. No line key can start with `_`, so it never
 * names a real line; the line page sends it on to the shot's line, or to the
 * presentation for a shot on no line.
 */
export const LOOSE_LINE_KEY = "_";

/** A shot whose line is not known here (the bell), or that belongs to no line. */
export const looseShotPath = (ref: string, shotKey: string) => shotPath(ref, LOOSE_LINE_KEY, shotKey);

/** The deck: the slides an editor works from. Every pipeline role, editors once the concept is released. */
export const deckPath = (ref: string) => `${briefPath(ref)}/deck`;
